import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  DateRangePicker,
  useToaster,
  Pagination,
  IconButton
} from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import { MdFilterList } from 'react-icons/md';
import { EyeRound as EyeRoundIcon } from '@rsuite/icons';
import { Icon } from '@rsuite/icons';
import { format, parseISO } from 'date-fns';
import * as XLSX from 'xlsx';

import DrawerView from './DrawerView';
import UserListDrawer from './DrawerUsers';
import { fetchGlobalWorkModes } from '../../../services/combinedModes.service';
import { getAdminUsers } from '@/services/user.service';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const MODE_DISPLAY_NAMES = {
  WFH: 'Home Office',
  OnSiteWithoutCheckin: 'On Site Without Checkin',
  OnSiteWithCheckin: 'On Site With Checkin',
  ThirdPartySite: 'Third Party Site',
  Leave: 'Leave',
  Untraced: 'Untraced',
  Other: 'Other'
};

const GlobalWorkModesDataTable = () => {
  const dispatch = useDispatch();
  const toaster = useToaster();
  const { data, loading } = useSelector(state => state.combinedModes);
  const { data: users, loading: usersLoading } = useSelector(state => state.users);

  const [showDrawer, setShowDrawer] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [filteredData, setFilteredData] = useState([]);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const first = today.getDate() - today.getDay();
    const last = first + 6;

    const start = new Date(today.setDate(first));
    start.setHours(0, 0, 0, 0);

    const end = new Date(today.setDate(last));
    end.setHours(23, 59, 59, 999);

    return [start, end];
  });
  const [filters, setFilters] = useState({ timeslot: '', mode: '' });

  const [drawerUsersOpen, setDrawerUsersOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    dispatch(getAdminUsers());
  }, [dispatch]);

  const applyFilters = (dataToFilter, searchTerm, currentFilters) => {
    let filtered = [...dataToFilter].map(item => ({
      ...item,
      formattedDate: item.date ? format(parseISO(item.date), 'yyyy-MM-dd') : '',
      displayMode: MODE_DISPLAY_NAMES[item.mode] || item.mode,
      mode: item.mode,
      timeslot: item.timeslot
    }));

    if (searchTerm.trim()) {
      const searchStr = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        item =>
          item.displayMode.toLowerCase().includes(searchStr) ||
          item.timeslot.toLowerCase().includes(searchStr) ||
          item.formattedDate.toLowerCase().includes(searchStr)
      );
    }

    if (currentFilters.timeslot) {
      filtered = filtered.filter(item => item.timeslot === currentFilters.timeslot);
    }

    if (currentFilters.mode) {
      filtered = filtered.filter(item => item.mode === currentFilters.mode);
    }

    return filtered;
  };

  useEffect(() => {
    if (dateRange && dateRange[0]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);

      const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
      endDate.setHours(23, 59, 59, 999);

      dispatch(fetchGlobalWorkModes(format(startDate, 'yyyy-MM-dd')));
    }
  }, [dateRange, dispatch]);

  useEffect(() => {
    const newFilteredData = applyFilters(data, searchKeyword, filters);
    setFilteredData(newFilteredData);
  }, [data, searchKeyword, filters]);

  const handleSearch = searchTerm => {
    setSearchKeyword(searchTerm);
    setPage(1);
  };

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({ timeslot: '', mode: '' });
    setPage(1);
  };

  const downloadExcel = (data, name) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  const handleExport = () => {
    const globalWorkModesToExport = filteredData.map(item => ({
      Date: item.formattedDate,
      Timeslot: item.timeslot,
      Mode: item.displayMode,
      'User Count': item.userCount
    }));

    const startDate = format(dateRange[0], 'yyyy-MM-dd');
    const endDate = format(dateRange[1], 'yyyy-MM-dd');
    downloadExcel(globalWorkModesToExport, `global-work-modes-${startDate}-to-${endDate}`);
  };

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  const handleViewUsers = row => {
    setSelectedRow({
      ...row,
      selectedMode: row.mode
    });
    setDrawerUsersOpen(true);
  };

  const { t } = useTranslation();

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            oneTap
            showOneCalendar
            hoverRange={'week'}
            ranges={[]}
            placeholder={t('Select Week')}
            editable={false}
          />
        </Stack>

        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <Icon as={MdFilterList} style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button appearance="primary" onClick={handleExport}>
            <ExportIcon style={{ marginRight: 8 }} />
            {t('Export')}
          </Button>
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 200, 400)}
        data={filteredData.slice((page - 1) * limit, page * limit)}
        loading={loading}
      >
        <Column flexGrow={2}>
          <HeaderCell>{t('Date')}</HeaderCell>
          <Cell dataKey="formattedDate" />
        </Column>
        <Column flexGrow={2}>
          <HeaderCell>{t('Timeslot')}</HeaderCell>
          <Cell dataKey="timeslot" />
        </Column>
        <Column flexGrow={3}>
          <HeaderCell>Mode</HeaderCell>
          <Cell dataKey="displayMode" />
        </Column>
        <Column flexGrow={2}>
          <HeaderCell>{t('User Count')}</HeaderCell>
          <Cell dataKey="userCount" />
        </Column>
        <Column width={80} fixed="right">
          <HeaderCell>{t('View')}</HeaderCell>
          <Cell>
            {rowData => (
              <IconButton
                size="xs"
                icon={<EyeRoundIcon />}
                title={t('More details')}
                onClick={() => handleViewUsers(rowData)}
              />
            )}
          </Cell>
        </Column>
      </Table>

      <div style={{ padding: 20 }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={['total', '-', 'limit', '|', 'pager', 'skip']}
          total={filteredData.length}
          limitOptions={[10, 30, 50]}
          limit={limit}
          activePage={page}
          onChangePage={setPage}
          onChangeLimit={handleChangeLimit}
        />
      </div>

      <DrawerView
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        users={users}
        usersLoading={usersLoading}
        toaster={toaster}
      />

      {drawerUsersOpen && selectedRow && (
        <UserListDrawer
          open={drawerUsersOpen}
          onClose={() => setDrawerUsersOpen(false)}
          selectedRow={selectedRow}
        />
      )}
    </>
  );
};

export default GlobalWorkModesDataTable;
